import { z } from "zod";
import type { PermissionKey } from "../permissions";
import { MCP_GRANT_REVOKE_REASONS, MCP_SCOPES, type McpGrantRevokeReason } from "./constants";

/**
 * The consent and "Connected apps" API (ADR 0009 sections 3 and 4): the shapes the SPA's consent
 * page and grant lists code against, and the server parses with. The routes are
 * `server/routes/mcpGrants.ts`; every path is under `/api/v2`.
 *
 * The consent flow: `/mcp-oauth/authorize` validates the client's request and sends the browser
 * to `MCP_CONSENT_PATH?request=<ref>`. The reference is opaque to the SPA (a signed, short-lived
 * copy of the validated request); the page passes it back verbatim. Identity and instance never
 * come from it: they come from the signed-in request (`x-instance-id`) that approves.
 */

/** The SPA route the authorization endpoint sends the browser to, with `?request=<ref>`. */
export const MCP_CONSENT_PATH = "/mcp-consent";
/** The query parameter carrying the pending-request reference. */
export const MCP_CONSENT_REQUEST_PARAM = "request";

/** A pending-request reference, as the SPA hands it back. Opaque; bounded so a probe stays small. */
const requestRefSchema = z.string().min(1).max(4096);

/** `GET /api/v2/mcp/consent?request=<ref>` */
export const mcpConsentQuerySchema = z.object({ request: requestRefSchema });

/**
 * The permissions that make a person a writer on an instance (ADR 0009 section 4): holding any one
 * of them, the consent page offers `skydata.write` as a separate checkbox, unticked by default.
 * They are the keys the write tools' routes check (section 6).
 */
export const MCP_WRITE_PERMISSION_KEYS = [
  "dataflows.write",
  "dataflows.validate",
  "schedules.write",
  "runs.start",
  "runs.retry",
] as const satisfies readonly PermissionKey[];

export function holdsMcpWritePermission(permissions: readonly string[]): boolean {
  return MCP_WRITE_PERMISSION_KEYS.some((key) => permissions.includes(key));
}

/**
 * `POST /api/v2/mcp/consent/approve`. `scopes` is what the person ticked: `skydata.read` always,
 * and `skydata.write` only if `offeredScopes` held it (a write permission on the instance);
 * anything else is refused 400. Absent means `["skydata.read"]`; write is never implied.
 */
export const mcpConsentApproveBodySchema = z
  .object({
    request: requestRefSchema,
    scopes: z.array(z.enum(MCP_SCOPES)).max(MCP_SCOPES.length).optional(),
  })
  .strict();
export type McpConsentApproveBody = z.infer<typeof mcpConsentApproveBodySchema>;

/** `POST /api/v2/mcp/consent/deny` */
export const mcpConsentDenyBodySchema = z.object({ request: requestRefSchema }).strict();
export type McpConsentDenyBody = z.infer<typeof mcpConsentDenyBodySchema>;

/**
 * Why the consent page cannot offer Approve, or null when it can:
 * - `platform_admin`: PlatformAdmin is refused in v1 (ADR 0009 section 4).
 * - `mcp_off`: the tenant's read switch or the instance's switch is off.
 * - `instance_locked`: this person already holds a live grant for this client on another
 *   instance; a re-consent stays on that instance (`existingGrant` names it).
 */
export const MCP_CONSENT_REFUSALS = ["platform_admin", "mcp_off", "instance_locked"] as const;
export type McpConsentRefusal = (typeof MCP_CONSENT_REFUSALS)[number];

/** What the consent page shows: the client named from `MCP_REDIRECT_URIS`, never itself. */
export interface McpConsentDetails {
  /** The display name of the redirect URI's `MCP_REDIRECT_URIS` entry ("Claude", "ChatGPT"). */
  clientName: string;
  /** The redirect URI's host, verbatim ("claude.ai"). */
  redirectHost: string;
  /** What the client asked for, unknown values already refused by the authorize endpoint. */
  requestedScopes: string[];
  /**
   * What the page may offer as checkboxes: `skydata.read`, and `skydata.write` when this person
   * holds a write permission on the instance (`MCP_WRITE_PERMISSION_KEYS`). Write is a separate
   * checkbox, unticked by default.
   */
  offeredScopes: string[];
  /**
   * True when write is offered but the tenant's write toggle is off: the grant may still hold
   * write, and it works only once a TenantAdmin turns the toggle on. The page says so.
   */
  writeSwitchedOff: boolean;
  /** The instance the person is in (`x-instance-id`), the one the grant will name. */
  instance: { id: string; name: string; tenantId: string; tenantDisplayName: string };
  /** A live grant this person already holds for this client, if any. */
  existingGrant: { id: string; name: string; instanceId: string; instanceName: string } | null;
  /**
   * True when Approve needs a sign-in no older than `recentSignInMaxAgeSeconds` and this one is
   * older: the page asks the provider to sign in again first (Approve would answer
   * `REAUTH_REQUIRED`). The first approval for a client needs it; so does a re-consent that
   * adds `skydata.write` to a live grant without it, which this flag cannot know in advance: the
   * page asks for the sign-in when Approve answers `REAUTH_REQUIRED`.
   */
  needsRecentSignIn: boolean;
  recentSignInMaxAgeSeconds: number;
  /** When the reference stops working; the person starts again from the AI app after it. */
  expiresAt: string;
  refusal: McpConsentRefusal | null;
}

/** Both consent decisions answer where the browser goes next: the client's redirect URI. */
export interface McpConsentDecisionResult {
  /** `redirect_uri?code=…&state=…&iss=…`, or `?error=access_denied&state=…&iss=…`. */
  redirectUrl: string;
}

export type McpGrantStatus = "active" | "revoked" | "expired";

/** One row of "Connected apps" and of the admins' grant lists. */
export interface McpGrantView {
  id: string;
  /** "Claude, <instance>" */
  name: string;
  /** The client's display name from `MCP_REDIRECT_URIS`, or "AI app" for an entry since removed. */
  clientName: string;
  instanceId: string;
  instanceName: string;
  tenantId: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: McpGrantRevokeReason | null;
  status: McpGrantStatus;
}

/** An admin list row adds who connected (the instance's and tenant's lists, not "mine"). */
export interface McpAdminGrantView extends McpGrantView {
  user: { id: string; email: string; displayName: string | null };
}

export interface McpGrantList<T extends McpGrantView = McpGrantView> {
  grants: T[];
}

/** Every revoke route answers how many live grants it ended. */
export interface McpRevokeResult {
  revoked: number;
}

export const mcpGrantIdParamsSchema = z.object({ id: z.uuid() });
/** `/tenants/:id/mcp/grants`: `id` is the tenant, as every other tenant route names it. */
export const mcpTenantGrantsParamsSchema = z.object({ id: z.uuid() });
export const mcpTenantGrantRevokeParamsSchema = z.object({ id: z.uuid(), grantId: z.uuid() });

/** `POST /api/v2/platform/mcp/revoke-all`: a typed confirmation, so a stray call does nothing. */
export const mcpRevokeAllBodySchema = z.object({ confirm: z.literal("REVOKE ALL") }).strict();

export function isMcpGrantRevokeReason(value: string): value is McpGrantRevokeReason {
  return (MCP_GRANT_REVOKE_REASONS as readonly string[]).includes(value);
}
